import './PlayerPerformance.scss';
// The shared Sessions components (pitch / goal charts, kick info card, session previews, tiles) are styled by Sessions.scss, which
// nothing else imports: load it here, after the page's own styles, so its rules sit where they always did in the cascade.
import 'pages/Sessions/Sessions.scss';

import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, useHistory } from 'react-router-dom';
import { AppPageHeader, AppSectionErrorBoundary, LoadingOverlay } from 'components/ui';
import { Authorization } from 'components/Authorization';
import { AppPermissions } from 'utilities/Constants';
import { useStores } from 'index';
import { t } from 'utilities/TranslationUtils';
import PlayerSelectionScreen from './PlayerSelectionScreen';
import PlayerPerformanceView from './PlayerPerformanceView';
import PlayerHeader from './PlayerHeader';
import SnapshotInfoContent from './SnapshotInfoContent';
import { sessionPlayerUrl } from 'routePaths';
import { GENERAL_SESSION_TYPE, playerSessionTypeTabs, presetsWithData } from './playerPerformanceUtils';

const DEFAULT_SESSION_TYPE = GENERAL_SESSION_TYPE;

/** Default session-type tab for a player: most-active kind, else FreeKicks. */
function defaultSessionType(measurements: Parameters<typeof playerSessionTypeTabs>[0], playerId: string): string {
  const tabs = playerSessionTypeTabs(measurements, playerId);
  return tabs.length > 0 ? tabs[0].filterValue : DEFAULT_SESSION_TYPE;
}

const PlayerPerformancePage: React.FunctionComponent = observer(() => {
  const { playerPerformanceStore: store, apiServiceAxiosInstance: api } = useStores();
  const location = useLocation();
  const history = useHistory();
  const [urlInitDone, setUrlInitDone] = useState(false);
  const hasTrackedPageOpen = useRef(false);
  const lastTrackedPlayerId = useRef<string | null>(null);

  // Load selection data once. Honor one-shot force-refresh knobs from the URL:
  //   ?cache=false  → bypass the BFF snapshot cache;  ?refresh=true → trigger snapshot regeneration.
  useEffect(() => {
    if (store.players.length === 0) {
      const sp = new URLSearchParams(location.search);
      store.LoadSelectionData(api, { bypassCache: sp.get('cache') === 'false', refresh: sp.get('refresh') === 'true' });
    }
  }, []);

  useEffect(() => {
    if (hasTrackedPageOpen.current) return;
    hasTrackedPageOpen.current = true;
    store.trackUsage(api, { action: 'MainPageOpened' });
  }, [api, store]);

  useEffect(() => {
    const playerId = store.focusedPlayerId;
    if (!playerId) {
      lastTrackedPlayerId.current = null;
      return;
    }

    if (lastTrackedPlayerId.current === playerId) return;

    lastTrackedPlayerId.current = playerId;
    store.trackUsage(api, {
      action: 'PlayerOpened',
      playerId,
      sessionType: store.selectedSessionType,
    });
  }, [api, store, store.focusedPlayerId, store.selectedSessionType]);

  // Load the focused player's heavy snapshot (measurements + kindStats) on focus and on deep-link.
  // The store caches per-player, polls on 202, and no-ops when already loaded.
  // Trusted users can force-refresh a specific player via URL params (?refresh=true / ?cache=false) —
  // read at fire time so it's one-shot per selection (the store→URL sync strips them afterwards).
  useEffect(() => {
    if (!store.focusedPlayerId) return;
    const sp = new URLSearchParams(location.search);
    store.LoadPlayerData(api, store.focusedPlayerId, {
      refresh: sp.get('refresh') === 'true',
      bypassCache: sp.get('cache') === 'false',
    });
  }, [api, store, store.focusedPlayerId]);

  // Keep store focus in sync with ?player= + ?type= after data is available.
  useEffect(() => {
    if (store.players.length === 0) return;

    const sp = new URLSearchParams(location.search);
    const id = sp.get('player');
    const type = sp.get('type');

    if (!id) {
      if (store.focusedPlayerId) {
        store.clearFocus();
      }

      if (!urlInitDone) {
        setUrlInitDone(true);
      }

      return;
    }

    if (!store.players.some((p) => p.id === id)) {
      if (store.focusedPlayerId) {
        store.clearFocus();
      }

      if (!urlInitDone) {
        setUrlInitDone(true);
      }

      return;
    }

    const resolvedType = type ?? defaultSessionType(store.measurements, id);

    if (store.focusedPlayerId !== id || store.selectedSessionType !== resolvedType) {
      store.focusPlayer(id, resolvedType);
    }

    if (!urlInitDone) {
      setUrlInitDone(true);
    }
  }, [location.search, store.measurements, store.players, urlInitDone]);

  // Keep ?player= + ?type= in sync with store state.
  useEffect(() => {
    if (!urlInitDone) return;
    const params = new URLSearchParams();
    if (store.focusedPlayerId) {
      params.set('player', store.focusedPlayerId);
      params.set('type', store.selectedSessionType);
    }
    const search = params.toString();
    if (search !== location.search.replace(/^\?/, '')) history.replace({ search });
  }, [store.focusedPlayerId, store.selectedSessionType, urlInitDone, location.search]);

  const handleFocusPlayer = (playerId: string, sessionTypeFilter?: string): void => {
    const filterValue = sessionTypeFilter ?? defaultSessionType(store.measurements, playerId);
    const params = new URLSearchParams({ player: playerId, type: filterValue });

    history.push({ search: params.toString() });
    store.focusPlayer(playerId, filterValue);
  };

  const handleFocusTeam = (teamKey: string): void => {
    history.replace({ search: '' });
    store.setTeamFilterActive(true);
    store.setSelectedTeamKey(teamKey);
    store.clearFocus();
  };

  const handleSelectSessionType = (filterValue: string): void => {
    store.setSessionType(filterValue);
    if (!store.focusedPlayerId) return;

    store.trackUsage(api, {
      action: 'SessionTypeSelected',
      playerId: store.focusedPlayerId,
      sessionType: filterValue,
    });
  };

  const snapshotInfo = store.snapshotMeta ? (
    <SnapshotInfoContent
      meta={store.snapshotMeta}
      focusedPlayerId={store.focusedPlayerId}
      focusedPlayerName={store.focusedPlayer?.name}
      measurements={store.measurements}
    />
  ) : (
    ''
  );

  // Timeframe pills that would show nothing for this player are hidden (All time always stays).
  const focusedId = store.focusedPlayer?.id;
  const availablePresets = useMemo(
    () => presetsWithData(focusedId ? store.measurements.filter((m) => m.playerId === focusedId) : []),
    [store.measurements, focusedId],
  );

  if (store.isLoading && store.players.length === 0) {
    return <LoadingOverlay isVisible />;
  }

  if (store.loadError) {
    return (
      <div className='player-performance-error' data-test='pp-error'>
        <p>{store.loadError}</p>
      </div>
    );
  }

  return (
    <AppSectionErrorBoundary>
      <div className='player-performance-page' data-test='player-performance-page'>
        {store.focusedPlayer ? (
          <PlayerHeader
            player={store.focusedPlayer}
            infoContent={snapshotInfo}
            onFocusTeam={handleFocusTeam}
            timeframe={store.timeframe}
            onTimeframeChange={(next) => store.setTimeframe(next)}
            availablePresets={availablePresets}
          />
        ) : (
          <AppPageHeader title={t('playerPerformance.pageTitle')} leftAlign tooltipContent={snapshotInfo} />
        )}

        {store.focusedPlayerId ? (
          // The identity header stays visible; only the report area shows a spinner on first load
          // (a later background refresh keeps the cached view visible).
          <PlayerPerformanceView
            sessionTypeFilter={store.selectedSessionType}
            onSelectSessionType={handleSelectSessionType}
            isLoading={store.isLoadingPlayer && !store.loadedPlayerIds.has(store.focusedPlayerId)}
            loadError={store.loadedPlayerIds.has(store.focusedPlayerId) ? null : store.playerLoadError}
            notReady={store.loadedPlayerIds.has(store.focusedPlayerId) ? false : store.playerNotReady}
            onRetry={() => { if (store.focusedPlayerId) store.LoadPlayerData(api, store.focusedPlayerId, { refresh: true }); }}
          />
        ) : (
          <PlayerSelectionScreen
            players={store.players}
            sessions={store.sessions}
            measurements={store.measurements}
            teamFilterActive={store.teamFilterActive}
            selectedTeamKey={store.selectedTeamKey}
            onToggleTeamFilter={(active) => store.setTeamFilterActive(active)}
            onSelectTeam={(key) => store.setSelectedTeamKey(key)}
            onClearTeamFilter={() => store.clearTeamFilter()}
            onFocusPlayer={handleFocusPlayer}
            onOpenSession={(sessionId, playerId) => history.push(sessionPlayerUrl(sessionId, playerId))}
          />
        )}
      </div>
    </AppSectionErrorBoundary>
  );
});

export default Authorization(PlayerPerformancePage, AppPermissions.OrgReportsAccess);
